
import React, { useMemo } from 'react';
import { useColorModeValue } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import VirtualizedSelect from './VirtualizedSelect';

/**
 * Generic dropdown/select for use in MultiChild breadcrumb, matching legacy UI.
 * Automatically switches to virtualized rendering for large datasets (>1000 items)
 * 
 * Props:
 * - staticEnum: array of objects (the options)
 * - dropDownValue: string (field name for value)
 * - displayField: string (field name for label, fallback if onDisplayDropdown not provided)
 * - onDisplayDropdown: function (optional, maps option to label)
 * - fieldValue: current selected value
 * - onChange: function (called with new value)
 * - noEmptyOption: if true, do not show empty option
 * - searchable: ignored (for legacy compatibility)
 * - useVirtualization: force use of virtualized select (default: auto based on size)
 * - ...rest: any other props
 */
const LoadingFieldDropdown = ({
  staticEnum = [],
  dropDownValue = 'id',
  displayField = 'name',
  onDisplayDropdown,
  fieldValue,
  onChange,
  noEmptyOption,
  useVirtualization = 'auto',
  ...rest
}) => {
  const { darkMode } = useSelector((state) => state.settings);

  const selectBg = darkMode ? '#23272F' : 'white';
  const selectColor = darkMode ? 'white' : 'black';
  const selectBorder = darkMode ? '#444950' : '#ccc';

  // Determine if we should use virtualization
  // Always use virtualized select to provide search functionality
  const shouldVirtualize = useMemo(() => {
    if (useVirtualization === false) return false;
    // Always use virtualized select for search capability
    return true;
  }, [useVirtualization]);

  // Use virtualized select for better performance with large datasets
  if (shouldVirtualize) {
    return (
      <VirtualizedSelect
        options={staticEnum}
        value={fieldValue}
        onChange={(newValue) => {
          onChange && onChange(newValue);
        }}
        getOptionLabel={(option) => 
          onDisplayDropdown ? onDisplayDropdown(option) : option[displayField]
        }
        getOptionValue={(option) => option[dropDownValue]}
        placeholder={noEmptyOption ? 'Select...' : 'Select...'}
        darkMode={darkMode}
        {...rest}
      />
    );
  }

  // Use standard HTML select for smaller datasets
  return (
    <select
      value={fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : ''}
      onChange={e => {
        const val = e.target.value;
        // Try to match the type of the value in staticEnum
        const match = staticEnum.find(opt => String(opt[dropDownValue]) === val);
        onChange && onChange(match ? match[dropDownValue] : val);
      }}
      style={{
        width: '100%',
        minWidth: 300,
        padding: 8,
        fontSize: 16,
        background: selectBg,
        color: selectColor,
        border: `1px solid ${selectBorder}`,
        borderRadius: 4,
        colorScheme: darkMode ? 'dark' : 'light',
      }}
      {...rest}
    >
      {!noEmptyOption && <option value="" style={{ background: selectBg, color: selectColor }}>Select...</option>}
      {staticEnum.map(option => (
        <option
          key={option[dropDownValue]}
          value={String(option[dropDownValue])}
          style={{ 
            background: selectBg, 
            color: selectColor,
          }}
        >
          {onDisplayDropdown
            ? onDisplayDropdown(option)
            : option[displayField]}
        </option>
      ))}
    </select>
  );
};

export default LoadingFieldDropdown;
